import React from 'react';
import { Container, Typography, Grid, Paper, Box, Button } from '@mui/material';
import { motion } from 'framer-motion';

const plans = [
  {
    title: 'Hotspot Only',
    price: '4% of hotspot revenue',
    priceType: 'percentage',
    maxCap: 'Ksh 3,000',
    features: [
      'Automated billing for hotspot networks',
      'User management for hotspot',
      'Usage tracking and analytics',
      'Basic customer support',
      'Unlimited locations/mikrotiks',
      'Allows Mikrotik remote management',
    ],
  },
  {
    title: 'Hotspot, PPPoE and Static IP',
    price: '4% of hotspot revenue + Ksh 30 per Active PPPoE user',
    priceType: 'hybrid',
    maxCap: 'Ksh 3,000',
    features: [
      'Everything in Hotspot Only plan',
      'Automated billing for PPPoE services',
      'Secure PPPoE user management',
      'Static IP assignment',
      'Advanced customer support',
      'Unlimited locations/mikrotiks',
      'Allows Mikrotik remote management',
    ],
  },
];

const Pricing = () => {
  const handleStartTrial = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  return (
    <Box
      component="section"
      id="pricing"
      sx={{
        py: { xs: 10, md: 18 },
        backgroundColor: '#f5f5f7',
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <Typography
            variant="h2"
            align="center"
            sx={{
              fontWeight: 700,
              letterSpacing: '-0.03em',
              background: 'linear-gradient(135deg, #19b594 0%, #0b74c1 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              mb: 2,
            }}
          >
            Flexible Pricing Plans
          </Typography>
          <Typography
            sx={{
              textAlign: 'center',
              color: 'rgba(29,29,31,0.65)',
              fontSize: 'clamp(1.05rem, 0.3vw + 0.5rem, 1.25rem)',
              maxWidth: '600px',
              mx: 'auto',
              mb: { xs: 6, md: 10 },
              lineHeight: 1.6,
            }}
          >
            Simple, transparent pricing that scales with your business. No hidden fees.
          </Typography>
        </motion.div>

        <Grid container spacing={{ xs: 3, md: 6 }} sx={{ alignItems: 'stretch' }}>
          {plans.map((plan, index) => (
            <Grid item xs={12} md={6} key={index} sx={{ display: 'flex' }}>
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{
                  duration: 0.7,
                  delay: index * 0.15,
                  ease: [0.25, 0.1, 0.25, 1],
                }}
                style={{ width: '100%', display: 'flex' }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 4, md: 5 },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    borderRadius: 4,
                    height: '100%',
                    width: '100%',
                    border: '1px solid rgba(0,0,0,0.06)',
                    backgroundColor: '#ffffff',
                    transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: '0 20px 48px rgba(0,0,0,0.1)',
                      borderColor: 'rgba(11,116,193,0.2)',
                    },
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 650,
                      color: '#1d1d1f',
                      letterSpacing: '-0.02em',
                      mb: 2,
                    }}
                  >
                    {plan.title}
                  </Typography>

                  <Typography
                    sx={{
                      fontWeight: 500,
                      color: 'rgba(29,29,31,0.8)',
                      fontSize: 'clamp(1rem, 0.3vw + 0.4rem, 1.2rem)',
                      mb: 2,
                      lineHeight: 1.4,
                    }}
                  >
                    {plan.price}
                  </Typography>

                  {plan.maxCap && (
                    <Box
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: '0.85rem', md: '0.9rem' },
                        px: 3,
                        py: 1,
                        backgroundColor: 'rgba(25,181,148,0.1)',
                        color: '#19b594',
                        borderRadius: 980,
                        display: 'inline-block',
                        mb: 3,
                      }}
                    >
                      Maximum: {plan.maxCap} per month{plan.priceType === 'hybrid' ? ' (for 4% portion)' : ''}
                    </Box>
                  )}

                  <Box
                    component="ul"
                    sx={{
                      listStyleType: 'none',
                      padding: 0,
                      m: 0,
                      mb: 3,
                      textAlign: 'left',
                      color: 'rgba(29,29,31,0.7)',
                      flexGrow: 1,
                      width: '100%',
                    }}
                  >
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex}>
                        <Typography
                          sx={{
                            fontSize: { xs: '0.9rem', md: '0.95rem' },
                            py: 0.75,
                            lineHeight: 1.5,
                            borderBottom: featureIndex < plan.features.length - 1 ? '1px solid rgba(0,0,0,0.04)' : 'none',
                          }}
                        >
                          {feature}
                        </Typography>
                      </li>
                    ))}
                  </Box>

                  {plan.maxCap && (
                    <Box
                      sx={{
                        mt: 'auto',
                        p: 2.5,
                        backgroundColor: '#f5f5f7',
                        borderRadius: 3,
                        textAlign: 'center',
                        width: '100%',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: { xs: '0.75rem', md: '0.8rem' },
                          color: 'rgba(29,29,31,0.6)',
                          lineHeight: 1.6,
                        }}
                      >
                        {plan.priceType === 'hybrid'
                          ? `You pay 4% of hotspot revenue up to a maximum of ${plan.maxCap} per month (you won't pay more than that for the percentage portion), plus Ksh 30 per Active PPPoE user. The price scales with you as your business grows and won't eat into your profit.`
                          : `You pay 4% of hotspot revenue up to a maximum of ${plan.maxCap} per month. You won't pay more than that, so the price scales with you as your business grows and won't eat into your profit.`
                        }
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.6, delay: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mt: { xs: 6, md: 8 },
              gap: 3,
            }}
          >
            <Box
              sx={{
                fontWeight: 500,
                fontSize: { xs: '0.95rem', md: '1.05rem' },
                px: { xs: 3, md: 5 },
                py: { xs: 1.5, md: 2 },
                backgroundColor: 'rgba(25,181,148,0.1)',
                color: '#19b594',
                borderRadius: 980,
                display: 'inline-block',
                textAlign: 'center',
              }}
            >
              We offer a 30-day free trial so you can explore all features risk-free.
            </Box>
            <Button
              variant="contained"
              onClick={handleStartTrial}
              sx={{
                fontWeight: 500,
                letterSpacing: '-0.005em',
                fontSize: { xs: '0.95rem', md: '1.05rem' },
                px: { xs: 4, md: 6 },
                py: { xs: 1.5, md: 1.75 },
                borderRadius: 980,
                background: 'linear-gradient(135deg, #0a66bb 0%, #19b594 100%)',
                color: '#ffffff',
                textTransform: 'none',
                boxShadow: '0 8px 24px rgba(10,102,187,0.25)',
                transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0b74c1 0%, #219cac 100%)',
                  transform: 'scale(1.03)',
                  boxShadow: '0 12px 32px rgba(10,102,187,0.35)',
                },
              }}
            >
              Start Your Trial
            </Button>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Pricing;
